// src/components/CommonComponents/EmptyState.js
import React from "react";
import PropTypes from "prop-types";

/**
 * Reusable EmptyState Component
 * 
 * Best Practices Applied:
 * 1. PropTypes for type safety
 * 2. Descriptive prop names and documentation
 * 3. Flexible customization (icon, title, message, actions)
 * 4. Size variants (small, default, large)
 * 5. Support for custom buttons/actions
 * 
 * @example
 * // Simple usage
 * <EmptyState 
 *   icon={ShoppingCart}
 *   title="No products found"
 *   message="Try adjusting your filters"
 * />
 * 
 * @example
 * // With retry action
 * <EmptyState 
 *   icon={AlertCircle}
 *   title="Failed to load"
 *   message="Connection error"
 *   onRetry={handleRetry}
 *   retryLabel="Try Again"
 * />
 * 
 * @example
 * // With custom action
 * <EmptyState 
 *   icon={Plus}
 *   title="No data"
 *   message="Create your first item"
 *   actionLabel="Create New"
 *   onAction={handleCreate}
 * />
 * 
 * @example
 * // Small inline variant
 * <EmptyState 
 *   size="small"
 *   icon={Info}
 *   message="No items"
 * />
 */
function EmptyState({
    icon: Icon,
    title,
    message,
    onRetry,
    onAction,
    retryLabel = "Try Again",
    actionLabel = "Create New",
    size = "default",
    showAction = true,
    className = ""
}) {
    // Small inline variant
    if (size === "small") {
        return (
            <p className={`text-center text-neutralGray text-sm mt-2 flex items-center justify-center gap-1 ${className}`}>
                {Icon && <Icon size={16} />}
                <span>{message}</span>
            </p>
        );
    }

    // Large variant (full page)
    const largeSizeClass = size === "large" ? "py-20" : "py-10";

    return (
        <div className={`col-span-full flex flex-col items-center justify-center ${largeSizeClass} text-gray-500 ${className}`}>
            {/* Icon */}
            {Icon && (
                <Icon className="w-20 h-20 mb-4 text-gray-300" strokeWidth={1.5} aria-hidden="true" />
            )}

            {/* Title (optional) */}
            {title && (
                <h3 className="text-xl font-semibold text-gray-700 mb-2 text-center">
                    {title}
                </h3>
            )}

            {/* Message */}
            {message && (
                <p className="text-gray-600 mb-6 text-center max-w-md">
                    {message}
                </p>
            )}

            {/* Action Buttons */}
            {showAction && (
                <div className="flex gap-3">
                    {/* Retry button (for error states) */}
                    {onRetry && (
                        <button
                            onClick={onRetry}
                            className="px-4 py-2 text-white rounded-lg shadow transition hover:opacity-90 active:scale-95"
                            style={{ backgroundColor: 'var(--color-primary)' }}
                            type="button"
                            aria-label={retryLabel}
                        >
                            {retryLabel}
                        </button>
                    )}

                    {/* Custom action button */}
                    {onAction && (
                        <button
                            onClick={onAction}
                            className="px-4 py-2 text-white rounded-lg shadow transition hover:opacity-90 active:scale-95"
                            style={{ backgroundColor: 'var(--color-secondary, var(--color-primary))' }}
                            type="button"
                            aria-label={actionLabel}
                        >
                            {actionLabel}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

// PropTypes for type safety
EmptyState.propTypes = {
    icon: PropTypes.elementType,
    title: PropTypes.string,
    message: PropTypes.string,
    onRetry: PropTypes.func,
    onAction: PropTypes.func,
    retryLabel: PropTypes.string,
    actionLabel: PropTypes.string,
    size: PropTypes.oneOf(["small", "default", "large"]),
    showAction: PropTypes.bool,
    className: PropTypes.string
};

EmptyState.defaultProps = {
    icon: null,
    title: null,
    message: "No data available",
    onRetry: null,
    onAction: null,
    retryLabel: "Try Again",
    actionLabel: "Create New",
    size: "default",
    showAction: true,
    className: ""
};

export default EmptyState;
